import React, { useRef, useEffect, useState } from 'react';
import { impactMetrics, caseStudies } from '../../data/results';
import useWordReveal from '../../hooks/useWordReveal';
import { ChartLine, CheckmarkCircuit, DatabaseCylinder, CircuitTrace, WifiSignal } from '../../components/Doodle/Doodle';
import { IsoChart, FloatingSphere } from '../../components/Svg3D/Svg3D';
import styles from './Results.module.css';

const AnimatedMetric = ({ value, isVisible }) => {
  const [displayValue, setDisplayValue] = useState('0');

  useEffect(() => {
    if (!isVisible) return;

    const match = value.match(/^([^0-9]*)([0-9.]+)(.*)$/);
    if (!match) { setDisplayValue(value); return; }

    const prefix = match[1];
    const target = parseFloat(match[2]);
    const suffix = match[3];
    const decimals = match[2].includes('.') ? match[2].split('.')[1].length : 0;
    const duration = 1500;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = (target * eased).toFixed(decimals);
      setDisplayValue(`${prefix}${current}${suffix}`);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [isVisible, value]);

  return <span>{displayValue}</span>;
};

const Results = () => {
  const [headingRef, isVisible] = useWordReveal({ threshold: 0.3 });
  const metricsRef = useRef(null);
  const cardsRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);

  const headingText = 'Built for Outcomes. Measured by Impact.';
  const words = headingText.split(' ');

  useEffect(() => {
    const metricsNode = metricsRef.current;
    const cardsNode = cardsRef.current;

    const metricsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMetricsVisible(true);
          metricsObserver.unobserve(metricsNode);
        }
      },
      { threshold: 0.1 }
    );

    const cardsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          cardsObserver.unobserve(cardsNode);
        }
      },
      { threshold: 0.1 }
    );

    if (metricsNode) metricsObserver.observe(metricsNode);
    if (cardsNode) cardsObserver.observe(cardsNode);

    return () => {
      metricsObserver.disconnect();
      cardsObserver.disconnect();
    };
  }, []);

  return (
    <section id="results" className={`${styles.section} grid-bg`}>
      <ChartLine position={{ top: '5%', left: '1%' }} size={170} theme="light" opacity={0.18} className={styles.doodle} />
      <CheckmarkCircuit position={{ bottom: '4%', right: '2%' }} size={150} theme="light" opacity={0.16} className={styles.doodle} />
      <DatabaseCylinder position={{ top: '40%', right: '1%' }} size={120} theme="light" opacity={0.14} className={styles.doodle} />
      <CircuitTrace position={{ bottom: '20%', left: '2%' }} size={140} theme="light" opacity={0.15} className={styles.doodle} />
      <WifiSignal position={{ top: '8%', right: '5%' }} size={100} theme="light" opacity={0.12} className={styles.doodle} />
      <IsoChart position={{ top: '18%', left: '5%' }} size={160} opacity={0.5} />
      <FloatingSphere position={{ bottom: '28%', right: '6%' }} size={140} opacity={0.45} />
      <div className="container">
        <div className={styles.header}>
          <span className={styles.sectionTag}>RESULTS</span>
          <div
            ref={headingRef}
            className={`word-reveal${isVisible ? ' visible' : ''}`}
          >
            <h2 className={styles.heading}>
              {words.map((word, i) => (
                <span
                  key={i}
                  className="word"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {word}
                </span>
              ))}
            </h2>
          </div>
        </div>

        <div ref={metricsRef} className={styles.metricsRow}>
          {impactMetrics.map((metric, index) => (
            <React.Fragment key={index}>
              {index > 0 && <div className={styles.metricDivider} />}
              <div
                className={`${styles.metricItem} ${metricsVisible ? styles.metricVisible : ''}`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <span className={styles.metricValue}><AnimatedMetric value={metric.value} isVisible={metricsVisible} /></span>
                <span className={styles.metricLabel}>{metric.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div ref={cardsRef} className={styles.cardsGrid}>
          {caseStudies.map((study, index) => (
            <div
              key={index}
              className={`${styles.card} ${cardsVisible ? styles.cardVisible : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <span className={styles.industry}>{study.industry}</span>
              <div className={styles.cardBody}>
                <div className={styles.cardSection}>
                  <span className={styles.cardLabel}>Challenge</span>
                  <p className={styles.cardText}>{study.challenge}</p>
                </div>
                <div className={styles.cardSection}>
                  <span className={styles.cardLabel}>Solution</span>
                  <p className={styles.cardText}>{study.solution}</p>
                </div>
                <div className={styles.resultBlock}>
                  <p className={styles.resultText}>{study.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Results;
